import * as XLSX from 'xlsx';
import { emptyStringsToNull } from '../common/utils';
import { MIN_MANUFACTURE_CODE_LENGTH } from '../types';

export const FIELDS_MAPPER = {
	'CODICE DEALER': 'dealer',
	'CODICE COSTRUTTORE DEL VEICOLO': 'codice_costruttore',
	'NR. ORDINE COMMERCIALE': 'nr_ordine',
	'CODICE COSTR. COLORE ESTERNO': 'codice_colore_esterno',
	'CODICE COSTR. COLORE INTERNO': 'codice_colore_interno',
	'CODICI OPT AGGIUNTIVI': 'optional_list',
	'SETTIMANA DI PRODUZIONE': 'settimana_prod',
	"DATA DI PREVISTA DISPONIBILITA'": 'data_disp',
	"CITTA'": 'city',
	'DATA TELAIO': 'data_telaio',
	TELAIO: 'telaio',
	'DATA IMMATRICOLAZIONE': 'data_immatricolazione',
	TARGA: 'targa',
	'VECCHIO BO (RTC)': 'id_bo',
	"GIORNO DISPONIBILITA' HORIZON": 'giorno_disp',
	PROMO: 'promo',
	'NOTE PROMO': 'promo_note',
	'NOTE GENERICHE': 'note',
	TAG: 'visibility_tag',
} as const;

export type ImportField = typeof FIELDS_MAPPER[keyof typeof FIELDS_MAPPER];

export const IMPORT_COLUMNS = Object.keys(
	FIELDS_MAPPER
) as (keyof typeof FIELDS_MAPPER)[];

export const NULLABLE_FIELDS = [
	'settimana_prod',
	'data_disp',
	'city',
	'data_telaio',
	'telaio',
	'data_immatricolazione',
	'targa',
	'id_bo',
	'giorno_disp',
	'promo',
	'promo_note',
	'note',
	'visibility_tag',
] as const;

type NullableField = typeof NULLABLE_FIELDS[number];
type RequiredStringField = Exclude<
	ImportField,
	'optional_list' | NullableField
>;

export type ImportedVehicleRow = Record<RequiredStringField, string> &
	Record<NullableField, string | null> & {
		rowId: string;
		optional_list: string[];
		codice_eurotax?: string;
		colore_esterno_desc?: string;
		colore_interno_desc?: string;
	};

export type FieldValidator = (value: unknown) => boolean;

const isNonEmpty: FieldValidator = (value) =>
	typeof value === 'string' && value.trim().length > 0;

export const VALIDATOR_FIELD_MAPPER: Partial<
	Record<ImportField, FieldValidator>
> = {
	codice_costruttore: (value) =>
		typeof value === 'string' &&
		value.trim().length >= MIN_MANUFACTURE_CODE_LENGTH,
	codice_colore_esterno: isNonEmpty,
	// codice_colore_interno: obbligatorio solo se il listino ha colori interni,
	// quindi si verifica dopo il caricamento del listino
	nr_ordine: isNonEmpty,
};

export const isFieldValid = (field: ImportField, value: unknown): boolean =>
	(VALIDATOR_FIELD_MAPPER[field] ?? (() => true))(value);

export const invalidFields = (row: ImportedVehicleRow): ImportField[] =>
	(Object.keys(VALIDATOR_FIELD_MAPPER) as ImportField[]).filter(
		(field) => !isFieldValid(field, row[field])
	);

export const isRowValid = (row: ImportedVehicleRow): boolean =>
	invalidFields(row).length === 0;

export const MAX_IMPORT_ROWS = 250;

export const formatCell = (value: unknown): string => {
	if (value === null || value === undefined) return '';
	return String(value).trim();
};

export const parseColorCell = (
	value: unknown
): { code: string; description: string } => {
	const raw = formatCell(value);
	const sep = raw.indexOf(' - ');
	if (sep === -1) return { code: raw, description: '' };
	return {
		code: raw.slice(0, sep).trim(),
		description: raw.slice(sep + 3).trim(),
	};
};

export const parseCodeList = (value: unknown): string[] =>
	formatCell(value)
		.split(',')
		.map((code) => code.trim())
		.filter(Boolean);

const isBlankRow = (raw: Record<string, unknown>): boolean =>
	Object.keys(FIELDS_MAPPER).every((column) => formatCell(raw[column]) === '');

export function mapExcelRows(
	rawRows: Record<string, unknown>[]
): ImportedVehicleRow[] {
	const filledRows = rawRows.filter((raw) => !isBlankRow(raw));

	if (filledRows.length > MAX_IMPORT_ROWS) {
		throw new Error(
			`Il file contiene ${filledRows.length} righe: il massimo consentito è ${MAX_IMPORT_ROWS}`
		);
	}

	return filledRows.map((raw) => {
		const row = { rowId: crypto.randomUUID() } as ImportedVehicleRow;
		for (const [column, field] of Object.entries(FIELDS_MAPPER)) {
			if (field === 'optional_list') {
				row.optional_list = parseCodeList(raw[column]);
			} else if (field === 'codice_colore_esterno') {
				const { code, description } = parseColorCell(raw[column]);
				row.codice_colore_esterno = code;
				if (description) row.colore_esterno_desc = description;
			} else if (field === 'codice_colore_interno') {
				const { code, description } = parseColorCell(raw[column]);
				row.codice_colore_interno = code;
				if (description) row.colore_interno_desc = description;
			} else {
				row[field as Exclude<ImportField, 'optional_list'>] = formatCell(
					raw[column]
				);
			}
		}
		return emptyStringsToNull(row, NULLABLE_FIELDS);
	});
}

export async function parseImportExcel(
	file: File
): Promise<ImportedVehicleRow[]> {
	const buffer = await file.arrayBuffer();
	const workbook = XLSX.read(buffer);
	const sheetName = workbook.SheetNames[0];
	if (!sheetName) {
		throw new Error('Il file non contiene fogli di lavoro');
	}

	const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
		workbook.Sheets[sheetName],
		{ raw: false, dateNF: 'dd/mm/yyyy', defval: null }
	);

	return mapExcelRows(rawRows);
}
